import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button.tsx';
import { Field, TextInput } from '../components/Field.tsx';
import { ExperimentStatusWord, ThresholdWord } from '../components/StatusWord.tsx';

const STEPS = [
  {
    n: '01',
    title: 'Name the test',
    body: 'Audience, message, offer, and the count that would change your next step.',
  },
  {
    n: '02',
    title: 'Send a tracked link',
    body: 'Share one destination. Clicks are counted once per visitor.',
  },
  {
    n: '03',
    title: 'Count a real yes',
    body: 'Record the step you asked for. A like is not that step.',
  },
  {
    n: '04',
    title: 'Read the engine',
    body: 'Reached, not reached, or too early. The engine suggests. You confirm.',
  },
] as const;

function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [joined, setJoined] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setPending(true);
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || body.ok === false) {
        throw new Error(body.error || 'Could not join the waitlist.');
      }
      setJoined(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not join the waitlist.');
    } finally {
      setPending(false);
    }
  }

  if (joined) {
    return (
      <p className="landing-form bg-paper px-5 py-5 text-base leading-snug text-ink" role="status">
        You are on the waitlist. We will write when a first seat is open.
      </p>
    );
  }

  return (
    <form
      className="landing-form bg-paper px-5 py-5 text-ink"
      onSubmit={(event) => void onSubmit(event)}
    >
      <Field label="Email">
        <TextInput
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <div className="mt-3">
        <Button type="submit" disabled={pending} className="w-full">
          {pending ? 'Joining…' : 'Join the waitlist'}
        </Button>
      </div>
      {error ? (
        <p className="mt-3 text-base text-danger" role="alert">
          {error}
        </p>
      ) : null}
      <p className="mt-3 text-base leading-snug text-ink-muted">
        Your address is stored so we can offer a first seat. It is not a public list.
      </p>
    </form>
  );
}

function ExperimentNotebook() {
  return (
    <aside
      id="notebook"
      className="landing-frame flex bg-paper text-ink shadow-card"
      aria-label="A live experiment notebook"
    >
      <div className="w-1.5 shrink-0 bg-accent-600" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-4 px-5 pt-5">
          <p className="font-score text-xs uppercase tracking-[0.16em] text-ink-subtle">
            Open page
          </p>
          <ExperimentStatusWord status="running" />
        </div>

        <div className="px-5 pb-6 pt-3">
          <h2 className="text-[1.75rem] leading-none">First seat</h2>
          <p className="mt-4 text-[1.0625rem] leading-7 text-ink-muted">
            Today the page reads <ThresholdWord state="pending" />. Later it will read reached, or
            not reached. One test, still running.
          </p>

          <dl className="mt-6">
            <div className="flex gap-4 border-t border-ink/10 py-3">
              <dt className="w-20 shrink-0 font-score text-xs uppercase tracking-[0.14em] text-ink-subtle">
                Audience
              </dt>
              <dd className="min-w-0 text-base leading-snug">
                People who would run one demand test
              </dd>
            </div>
            <div className="flex gap-4 border-t border-ink/10 py-3">
              <dt className="w-20 shrink-0 font-score text-xs uppercase tracking-[0.14em] text-ink-subtle">
                Message
              </dt>
              <dd className="min-w-0 text-base leading-snug">Score it on yeses, not charts.</dd>
            </div>
            <div className="flex gap-4 border-y border-ink/10 py-3">
              <dt className="w-20 shrink-0 font-score text-xs uppercase tracking-[0.14em] text-ink-subtle">
                Offer
              </dt>
              <dd className="min-w-0 text-base leading-snug">Join the waitlist for a first seat</dd>
            </div>
          </dl>

          <p className="mt-5 text-base leading-snug text-ink-muted">
            What counts is a waitlist join. Yeses against the count you set. Likes do not count.
          </p>
        </div>
      </div>
    </aside>
  );
}

export default function LandingPage() {
  return (
    <div className="landing min-h-screen bg-paper">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <header className="bg-ink text-paper">
        <div className="mx-auto flex max-w-6xl items-end justify-between gap-4 px-5 py-4 lg:px-8">
          <div>
            <p className="font-serif text-[1.375rem] leading-none">Austin OS</p>
            <p className="mt-1.5 font-score text-xs uppercase tracking-[0.16em] text-accent-200">
              First seat · not a live product
            </p>
          </div>
          <nav aria-label="Primary" className="shrink-0">
            <Link
              to="/login"
              className="inline-flex min-h-11 items-center whitespace-nowrap font-score text-[0.75rem] uppercase tracking-[0.16em] text-paper underline decoration-accent-200 underline-offset-4"
            >
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <main id="main">
        <section id="waitlist" className="bg-ink text-paper" aria-labelledby="hero-heading">
          <div className="mx-auto max-w-6xl lg:grid lg:grid-cols-2 lg:items-end lg:gap-14 lg:px-8 lg:pb-16 lg:pt-12">
            <div className="flex min-h-[calc(100svh-4.75rem)] flex-col justify-between px-5 pb-8 pt-7 lg:min-h-0 lg:px-0 lg:pb-0 lg:pt-0">
              <div>
                <h1
                  id="hero-heading"
                  className="max-w-[10ch] text-[2.85rem] leading-[0.92] sm:text-6xl lg:text-[4.75rem]"
                >
                  Score it on yeses, not charts.
                </h1>
                <p className="mt-6 max-w-[24ch] text-lg leading-[1.3] text-accent-100">
                  A live demand test. People say they want it, or they walk. That is the score.
                </p>
                <div className="mt-8">
                  <WaitlistForm />
                </div>
              </div>
              <p className="mt-10 font-serif text-[1.5rem] leading-tight italic lg:mt-12 lg:text-[1.75rem]">
                Analytics never bought a thing.
              </p>
            </div>

            <div className="px-5 pb-10 lg:px-0 lg:pb-0">
              <ExperimentNotebook />
            </div>
          </div>
        </section>

        <section aria-labelledby="how-heading">
          <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8 lg:py-16">
            <h2 id="how-heading" className="text-[1.85rem] leading-none lg:text-4xl">
              How it works
            </h2>
            <p className="mt-3 max-w-[28ch] text-lg leading-snug text-ink-muted">
              One test at a time. You decide what to do next.
            </p>

            <ol className="mt-8 border-t border-ink lg:grid lg:grid-cols-4 lg:border-t-0">
              {STEPS.map((step) => (
                <li
                  key={step.n}
                  className="border-b border-ink py-5 lg:border-b-0 lg:border-r lg:px-5 lg:py-0 lg:first:pl-0 lg:last:border-r-0 lg:last:pr-0"
                >
                  <p className="font-serif text-lg text-accent-600">{step.n}</p>
                  <h3 className="mt-1 text-[1.35rem] leading-tight">{step.title}</h3>
                  <p className="mt-2 text-base leading-snug text-ink-muted">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section aria-labelledby="yes-heading" className="border-t border-ink">
          <div className="mx-auto max-w-6xl lg:grid lg:grid-cols-2">
            <article className="px-5 py-12 lg:border-r lg:border-ink lg:px-8 lg:py-16">
              <h2 id="yes-heading" className="text-[1.85rem] leading-none">
                What a yes is
              </h2>
              <p className="mt-4 max-w-[24ch] text-lg leading-snug text-ink-muted">
                The step you asked someone to take: a purchase, a waitlist join, or a reply.
              </p>
            </article>
            <article className="bg-ink px-5 py-12 text-paper lg:px-8 lg:py-16">
              <h2 className="text-[1.85rem] leading-none">What a yes is not</h2>
              <p className="mt-4 max-w-[24ch] text-lg leading-snug text-accent-100">
                A like is not a yes. An impression is not a yes. The engine will not conclude from
                them.
              </p>
            </article>
          </div>
        </section>

        <section aria-labelledby="not-heading" className="border-t border-ink">
          <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8 lg:py-16">
            <h2 id="not-heading" className="text-[1.85rem] leading-none lg:text-4xl">
              What this is not
            </h2>
            <p className="mt-4 max-w-[32ch] text-lg leading-snug text-ink-muted">
              This is not a waitlist tool, a CRM, an analytics suite, or a scheduler. It is one
              demand test at a time. Reached is not product-market fit. Not reached does not mean
              the idea is invalid. Too early means keep running the same test.
            </p>
            <p className="mt-8">
              <a
                href="#waitlist"
                className="inline-flex min-h-11 items-center text-base text-accent-600 underline underline-offset-4"
              >
                Join the waitlist
              </a>
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-ink">
        <div className="mx-auto flex max-w-6xl flex-col gap-1 px-5 py-6 font-score text-[0.6875rem] uppercase tracking-[0.14em] text-ink-subtle sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <p>A first seat, when one is open. Not a live product URL yet.</p>
          <Link
            to="/login"
            className="inline-flex min-h-11 items-center underline underline-offset-4"
          >
            Sign in
          </Link>
        </div>
      </footer>
    </div>
  );
}
